import { useDispatch, useSelector } from "react-redux";

import { setModuleSelected, setProcesSelected } from "../../libs/redux/slices/MenuSlice/menuSlice";
import {
  setContextOverride,
  setExternal,
  setFiltersTracking,
  setNavFilter,
  setSortTracking,
} from "../../libs/redux/slices/trackingSlice/trackingSlice";
import { getMenuUser } from "../../libs/redux/slices/authSlice/authSlice";
import { getMipuestoPestanas, getTrackingOperaciones, setTabSolicitada } from "../../libs/redux/slices/Mipuesto/mipuestoSlice";

//Orden inicial del tracking (campos magicos, presentes en toda tabla). Al
//cambiar de proceso hay que volver a el: el orden del tracking de origen se
//guarda en estado compartido y, si el destino no tiene esa columna, la
//consulta revienta (p. ej. ESTGEPES, del listado de tareas, sobre MDOS).
const SORT_POR_DEFECTO = "-REGFECXX,-REGHORXX";


/**
 * Hook para navegar a otro proceso del menu aplicando filtros inducidos
 * al tracking de destino (hipervinculos tracking <-> DO).
 * @author Diego Serrano <diego.serrano@repremundo.com.co>
 * @since 2026-07-17
 */
export function useNavigateProcess() {
  const dispatch = useDispatch();
  const menuData = useSelector(getMenuUser);
  const mipuestoPestanas = useSelector(getMipuestoPestanas);
  const trackingOperaciones = useSelector(getTrackingOperaciones);

  /**
   * Navega al proceso indicado si el usuario lo tiene en su menu.
   * @param {string} moduleUrl  MODURLXX del modulo destino (ej. "Modimpxx")
   * @param {string} processUrl PRODURLX del proceso destino (ej. "Trackimp")
   * @param {Array}  filters    Filtros inducidos para el tracking destino
   * @param {Object} opciones   {soloProceso: true} = ir al proceso aunque exista como pestaña del puesto
   * @returns {boolean} true si navego, false si el proceso no esta en el menu
   */
  const handleNavigateProcess = (moduleUrl, processUrlPedido, filters, opciones = {}) => {
    //Regla de Diego (2026-09-24): el tracking de operaciones es el del CARGO. Los Ejecutivos de Cuenta
    //siempre abren Planeación Importaciones y los Especialistas Elaboración de Declaraciones, venga el
    //drill-down de donde venga (tableros, Mi Día, diagnóstico, alertas). Único punto de navegación.
    const esTrackingDo = moduleUrl === "Modimpxx" && (processUrlPedido === "Trackimp" || processUrlPedido === "Planeimp");
    const processUrl = esTrackingDo && trackingOperaciones ? trackingOperaciones : processUrlPedido;
    // Si el destino es una pestaña de Mi Puesto (workspace montado),
    // cambiar de PESTAÑA en vez de sacar al usuario al menú de procesos.
    // Los tableros del kit piden soloProceso (Diego 2026-09-15): un drill-down
    // siempre abre el tracking del sistema, nunca la pestaña del puesto.
    const pestanaDestino = opciones.soloProceso ? null : mipuestoPestanas.find(
      (tab) => tab.moduleUrl === moduleUrl && tab.processUrl === processUrl
    );
    if (pestanaDestino) {
      dispatch(setExternal(true));
      dispatch(setSortTracking(SORT_POR_DEFECTO));
      dispatch(setNavFilter({ target: `${moduleUrl}.${processUrl}`, filters, stamp: Date.now() }));
      dispatch(setFiltersTracking(filters));
      dispatch(setTabSolicitada(pestanaDestino.PESTORDX));
      return true;
    }
    let moduleItem = null;
    let processItem = null;
    menuData.forEach((menu) => {
      if (menu.MODURLXX !== moduleUrl) {
        return;
      }
      menu.CIMS0004.forEach((submenu) => {
        if (submenu.PRODURLX === processUrl) {
          moduleItem = menu;
          processItem = submenu;
        }
      });
    });
    if (!moduleItem || !processItem) {
      return false;
    }
    // Bandera external: evita que el Trackhea del proceso destino borre
    // los filtros inducidos al montar (mismo mecanismo de los deep-links;
    // Tracking la apaga al recibir la primera respuesta). No alcanza sola:
    // si el destino monta mas de una vez (p. ej. al cambiar de pestaña en
    // Mi Puesto) la bandera ya se apago y el segundo montaje los borra. El
    // navFilter, con el destino marcado, sobrevive a esos remontajes:
    // Trackhea lo re-aplica cuando la pantalla coincide.
    //Al salir de Mi Puesto hacia un proceso propio, el contexto embebido
    //(proceso real de la pestaña) caduca YA: si sobrevive al cambio, la
    //pantalla destino se resuelve contra el proceso de la pestaña y consulta
    //con sus filtros una tabla ajena (Diego 2026-09-14).
    dispatch(setContextOverride(null));
    dispatch(setExternal(true));
    dispatch(setSortTracking(SORT_POR_DEFECTO));
    dispatch(setNavFilter({ target: `${moduleUrl}.${processUrl}`, filters, stamp: Date.now() }));
    dispatch(setFiltersTracking(filters));
    dispatch(setModuleSelected(moduleItem));
    dispatch(setProcesSelected(processItem));
    return true;
  };

  return { handleNavigateProcess };
}
